import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../lib/supabase';
import { sendInverterFailureNotification } from '../lib/emailService';

const { FiX, FiSave, FiUpload, FiImage, FiFileText, FiCheck, FiAlertTriangle } = FiIcons;

const InverterCheckForm = ({ check, onClose, plants }) => {
  const [formData, setFormData] = useState({
    plant_id: check?.plant_id || '',
    inverter_name: check?.inverter_name || '',
    inverter_bmr_name: check?.inverter_bmr_name || '',
    serial_number: check?.serial_number || '',
    location: check?.location || '',
    check_result: check?.check_result || 'Bestanden',
    photo_url: check?.photo_url || '',
    notes: check?.notes || '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(check?.photo_url || null);
  const fileInputRef = useRef(null);

  // Form-Validierung 
  const validateForm = () => {
    const newErrors = {};
    if (!formData.plant_id) newErrors.plant_id = 'Anlage ist erforderlich';
    if (!formData.inverter_name.trim()) newErrors.inverter_name = 'Wechselrichter Name ist erforderlich';
    if (!formData.serial_number.trim()) newErrors.serial_number = 'Seriennummer ist erforderlich';
    if (!formData.location.trim()) newErrors.location = 'Ort ist erforderlich';
    if (!formData.check_result) newErrors.check_result = 'Prüfergebnis ist erforderlich';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Formular absenden
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      let photoUrl = formData.photo_url;

      // Foto hochladen, wenn vorhanden
      if (photoFile) {
        const fileExt = photoFile.name.split('.').pop();
        const fileName = `${Date.now()}.${fileExt}`;
        const filePath = `inverter-checks/${fileName}`;

        // Foto in Supabase Storage hochladen
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('pv-images')
          .upload(filePath, photoFile);

        if (uploadError) {
          console.error('Error uploading photo:', uploadError);
          throw uploadError;
        }

        // Öffentliche URL für das Bild erstellen
        const { data: { publicUrl } } = supabase.storage
          .from('pv-images')
          .getPublicUrl(filePath);

        photoUrl = publicUrl;
      }

      const now = new Date().toISOString();

      if (check) {
        // Bestehende Prüfung aktualisieren
        const { error } = await supabase
          .from('pv_inverter_checks_a7b3c9d8e2')
          .update({
            plant_id: formData.plant_id,
            inverter_name: formData.inverter_name,
            inverter_bmr_name: formData.inverter_bmr_name,
            serial_number: formData.serial_number,
            location: formData.location,
            check_result: formData.check_result,
            photo_url: photoUrl,
            notes: formData.notes.trim() || null,
            updated_at: now
          })
          .eq('id', check.id);

        if (error) {
          console.error('Error updating inverter check:', error);
          throw error;
        }

        // Eintrag zur Historie hinzufügen, wenn Änderungen vorgenommen wurden
        if (check.check_result !== formData.check_result || photoFile) {
          const { error: historyError } = await supabase
            .from('pv_inverter_history_a7b3c9d8e2')
            .insert({
              inverter_id: check.id,
              check_date: now,
              check_result: formData.check_result,
              photo_url: photoUrl,
              notes: formData.notes.trim() || null
            });

          if (historyError) {
            console.error('Error adding to history:', historyError);
            throw historyError;
          }

          // Wenn Status auf "Nicht bestanden" geändert wurde, E-Mail-Daten speichern
          if (formData.check_result === 'Nicht bestanden' && check.check_result !== 'Nicht bestanden') {
            try {
              await sendInverterFailureNotification({
                inverter_name: formData.inverter_name,
                serial_number: formData.serial_number,
                location: formData.location,
                notes: formData.notes
              });
            } catch (emailError) {
              console.error('Fehler beim Speichern der E-Mail-Daten:', emailError);
              // Fehler beim E-Mail-Datenspeichern sollte den Speichervorgang nicht blockieren
              // Wir loggen nur den Fehler und fahren fort
            }
          }
        }
      } else {
        // Neue Prüfung erstellen
        const { data: newInverter, error } = await supabase
          .from('pv_inverter_checks_a7b3c9d8e2')
          .insert({
            plant_id: formData.plant_id,
            inverter_name: formData.inverter_name,
            inverter_bmr_name: formData.inverter_bmr_name || null,
            serial_number: formData.serial_number,
            location: formData.location,
            check_result: formData.check_result,
            photo_url: photoUrl || null,
            notes: formData.notes.trim() || null,
            check_date: now,
            created_at: now,
            updated_at: now
          })
          .select()
          .single();

        if (error) {
          console.error('Error creating inverter check:', error);
          throw error;
        }

        // Erster Eintrag in der Historie
        if (newInverter) {
          const { error: historyError } = await supabase
            .from('pv_inverter_history_a7b3c9d8e2')
            .insert({
              inverter_id: newInverter.id,
              check_date: now,
              check_result: formData.check_result,
              photo_url: photoUrl || null,
              notes: formData.notes.trim() || null
            });

          if (historyError) {
            console.error('Error adding to history:', historyError);
            throw historyError;
          }
        }

        // E-Mail-Daten speichern, wenn die Prüfung nicht bestanden wurde
        if (formData.check_result === 'Nicht bestanden') {
          try {
            const plantName = plants.find(p => p.id === formData.plant_id)?.name || 'Unbekannt';
            await sendInverterFailureNotification({
              inverter_name: formData.inverter_name,
              serial_number: formData.serial_number,
              location: formData.location,
              notes: formData.notes
            });
          } catch (emailError) {
            console.error('Fehler beim Speichern der E-Mail-Daten:', emailError);
            // Fehler beim E-Mail-Datenspeichern sollte den Speichervorgang nicht blockieren
          }
        }
      }

      onClose(true); // true bedeutet: Daten neu laden
    } catch (error) {
      console.error('Error submitting inverter check:', error);
      alert('Fehler beim Speichern der Prüfung: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Foto auswählen
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setPhotoFile(file);

    // Vorschau anzeigen
    const reader = new FileReader();
    reader.onload = () => {
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Foto entfernen
  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setFormData({...formData, photo_url: ''});
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {check ? 'Wechselrichter bearbeiten' : 'Neuen Wechselrichter anlegen'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <SafeIcon icon={FiX} className="text-2xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Anlage auswählen */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Anlage *
              </label>
              <div className="relative">
                <SafeIcon icon={FiFileText} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <select
                  value={formData.plant_id}
                  onChange={(e) => setFormData({...formData, plant_id: e.target.value})}
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg appearance-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.plant_id ? 'border-red-300' : 'border-gray-300'}`}
                >
                  <option value="">Anlage auswählen</option>
                  {plants.map(plant => (
                    <option key={plant.id} value={plant.id}>{plant.name}</option>
                  ))}
                </select>
                <SafeIcon icon={FiIcons.FiChevronDown} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              </div>
              {errors.plant_id && <p className="text-red-500 text-sm mt-1">{errors.plant_id}</p>}
            </div>

            {/* Wechselrichter Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Wechselrichter Name *
              </label>
              <input
                type="text"
                value={formData.inverter_name}
                onChange={(e) => setFormData({...formData, inverter_name: e.target.value})}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.inverter_name ? 'border-red-300' : 'border-gray-300'}`}
                placeholder="z.B. SMA Sunny Tripower"
              />
              {errors.inverter_name && <p className="text-red-500 text-sm mt-1">{errors.inverter_name}</p>}
            </div>

            {/* Wechselrichter BMR Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                WR Name BMR (optional)
              </label>
              <input
                type="text"
                value={formData.inverter_bmr_name}
                onChange={(e) => setFormData({...formData, inverter_bmr_name: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="BMR Bezeichnung"
              />
            </div>

            {/* Seriennummer */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Seriennummer *
              </label>
              <input
                type="text"
                value={formData.serial_number}
                onChange={(e) => setFormData({...formData, serial_number: e.target.value})}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.serial_number ? 'border-red-300' : 'border-gray-300'}`}
                placeholder="z.B. SN123456789"
              />
              {errors.serial_number && <p className="text-red-500 text-sm mt-1">{errors.serial_number}</p>}
            </div>

            {/* Ort */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ort *
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.location ? 'border-red-300' : 'border-gray-300'}`}
                placeholder="z.B. Technikraum UG"
              />
              {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location}</p>}
            </div>

            {/* Prüfergebnis */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prüfergebnis *
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setFormData({...formData, check_result: 'Bestanden'})}
                  className={`flex items-center justify-center space-x-2 px-4 py-3 border rounded-lg ${formData.check_result === 'Bestanden' ? 'bg-green-50 border-green-300 text-green-700' : 'border-gray-300 hover:bg-gray-50'}`}
                >
                  <SafeIcon icon={FiCheck} className={formData.check_result === 'Bestanden' ? 'text-green-600' : 'text-gray-400'} />
                  <span>Bestanden</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({...formData, check_result: 'Nicht bestanden'})}
                  className={`flex items-center justify-center space-x-2 px-4 py-3 border rounded-lg ${formData.check_result === 'Nicht bestanden' ? 'bg-red-50 border-red-300 text-red-700' : 'border-gray-300 hover:bg-gray-50'}`}
                >
                  <SafeIcon icon={FiAlertTriangle} className={formData.check_result === 'Nicht bestanden' ? 'text-red-600' : 'text-gray-400'} />
                  <span>Nicht bestanden</span>
                </button>
              </div>
              {errors.check_result && <p className="text-red-500 text-sm mt-1">{errors.check_result}</p>}
            </div>
          </div>

          {/* Notizen */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notizen (optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              rows="3"
              placeholder="Optionale Notizen zum Wechselrichter"
            ></textarea>
          </div>

          {/* Foto Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Foto des Wechselrichters (optional)
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              {photoPreview ? (
                <div className="relative">
                  <img src={photoPreview} alt="Vorschau" className="max-h-60 mx-auto rounded-lg" />
                  <button
                    type="button"
                    onClick={removePhoto}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <SafeIcon icon={FiX} className="text-xs" />
                  </button>
                </div>
              ) : (
                <>
                  <SafeIcon icon={FiImage} className="text-gray-400 text-3xl mx-auto mb-2" />
                  <p className="text-gray-600 mb-2">Foto hier ablegen oder klicken zum Auswählen</p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                    id="photo-upload"
                    ref={fileInputRef}
                  />
                  <label
                    htmlFor="photo-upload"
                    className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer inline-flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiUpload} />
                    <span>Foto auswählen</span>
                  </label>
                </>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Abbrechen
            </button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={isSubmitting}
              className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors flex items-center space-x-2 disabled:opacity-70"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Speichern...</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiSave} />
                  <span>{check ? 'Aktualisieren' : 'Hinzufügen'}</span>
                </>
              )}
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default InverterCheckForm;